import React from 'react';
import { PDFDownloadLink, Page, Text, Image, View, Document, StyleSheet } from '@react-pdf/renderer';
import { apiGet } from 'services';
import { useSelector } from 'react-redux';
import { ArrayHelpers, SystemText } from '@salesperformanceint/e4shared';
import { e4Format, e4Formats, getYear } from 'helpers/dates';

// Create styles
const styles = StyleSheet.create({
    table: {
        alignItems: 'left',
        display: 'table',
        width: 520,
        borderWidth: 0,
    },
    tableRow: { margin: 'auto', display: 'flex', flexDirection: 'row', width: 520 },
    tableColFlag: { borderStyle: 'solid', borderWidth: 0, borderBottomWidth: 1, flexBasis: 20 },
    tableCol: {
        borderStyle: 'solid',
        marginLeft: 0,
        borderWidth: 0,
        borderBottomWidth: 1,
        alignItems: 'right',
        flexBasis: 70,
    },
    tableColDate: { borderStyle: 'solid', borderWidth: 0, borderBottomWidth: 1, flexBasis: 90, alignItems: 'right' },
    tableColFill: { borderStyle: 'solid', borderWidth: 0, borderBottomWidth: 1, flexGrow: 1 },
    tableColFlagNoEnd: { borderStyle: 'solid', borderWidth: 0, flexBasis: 20 },
    tableColNoEnd: { borderStyle: 'solid', marginLeft: 0, borderWidth: 0, alignItems: 'right', flexBasis: 70 },
    tableColDateNoEnd: { borderStyle: 'solid', borderWidth: 0, flexBasis: 90, alignItems: 'right' },
    tableColFillNoEnd: { borderStyle: 'solid', borderWidth: 0, flexGrow: 1 },
    tableCell: { fontSize: 10, paddingTop: 10, paddingBottom: 10, paddingLeft: 0, paddingRight: 0 },
    tableColExtra: { display: 'flex', flexDirection: 'row', paddingLeft: 10, paddingBottom: 5, marginRight: 10 },
    tableColExtraText: { flexBasis: 410 },
    tableColExtraTextBoth: { flexBasis: 378 },
    tableColExtraIcon: { flexBasis: 13 },
    tableColExtraRatings: { flexBasis: 20 },
    textSmall: { fontSize: 8, marginBottom: 5, marginRight: 5 },
    tableCellFill: { fontSize: 10, padding: 10, maxWidth: 300, textOverflow: 'ellipsis' },
    page: {
        flexDirection: 'column',
        backgroundColor: '#ebebeb',
        margin: 0,
        padding: 20,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        marginBottom: 20,
    },
    icon: {
        height: 8,
        width: 8,
        marginBottom: 5,
        marginRight: 2,
    },
    logo: {
        height: 25,
        marginRight: 5,
    },
    headerText: {},
    headerName: { flexGrow: 1, textAlign: 'right' },
    infoBox: {
        flexDirection: 'row',
        backgroundColor: '#ffffff',
        border: 'solid 1px #a0a0a0',
        borderRadius: 3,
        padding: 20,
        paddingBottom: 10,
        marginBottom: 20,
    },
    info: { width: 300 },
    infoList: { flexDirection: 'column' },
    label: {
        fontSize: 12,
        marginBottom: 3,
    },
    labelValue: {
        fontSize: 10,
        marginBottom: 10,
    },
    flag: {
        width: 20,
        height: 20,
        marginTop: 5,
    },
});

export const ReviewPDF = ({ appraisal, objectives, options }) => {
    const json = JSON.parse(appraisal?.json || '{}');
    const agreedValues = json.ratings || JSON.parse(json.appraisal?.ratings || appraisal.ratings || '[]');
    const st = (path, ...rest) => SystemText(`Reviews/Appraisals/PDF/${path}`, ...rest);
    const user = appraisal.participants.indexOf('U') >= 0;
    const manager = appraisal.participants.indexOf('M') >= 0;
    const both = appraisal.participants === 'UM';

    if (!ArrayHelpers.existsAndHasLength(json.competencies))
        return (
            <Document>
                <Page>
                    <View>
                        <Text>{st('Loading')}</Text>
                    </View>
                </Page>
            </Document>
        );

    const hasReviewCompetencies = json.competencies.some(c => c.review_Comment);
    const dateRange =
        getYear(objectives.from) === 2001
            ? st('From', e4Format(objectives.to))
            : st('FromTo', e4Format(objectives.from), e4Format(objectives.to));

    const title = appraisal.participants === 'U' ? 'User' : appraisal.participants === 'M' ? 'Manager' : 'Both';

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Image style={styles.logo} src="/dist/img/richardson-logo_sm.png"></Image>
                    <Text style={styles.headerText}>{st('Company')}</Text>
                    <Text style={styles.headerName}>{st(`Title/${title}`)}</Text>
                </View>
                <View style={styles.infoBox}>
                    <View style={styles.info}>
                        <Text style={styles.label}>{st('Employee')}</Text>
                        <Text style={styles.labelValue}>{appraisal.full_Name}</Text>
                        <Text style={styles.label}>{st('Manager')}</Text>
                        <Text style={styles.labelValue}>{appraisal.manager_Full_Name}</Text>
                    </View>
                    <View style={styles.info}>
                        <Text style={styles.label}>{st('ReviewDate')}</Text>
                        <Text style={styles.labelValue}>{e4Format(appraisal.submission)}</Text>
                        <Text style={styles.label}>{st('Scheduled')}</Text>
                        <Text style={styles.labelValue}>{e4Format(appraisal.scheduled)}</Text>
                    </View>
                </View>
                <View style={styles.infoBox}>
                    <View style={styles.table}>
                        {/* TableHeader */}
                        <View style={styles.tableRow}>
                            <View style={styles.tableColFlag}>
                                <Text style={styles.tableCell}></Text>
                            </View>
                            <View style={styles.tableColFill}>
                                <Text style={styles.tableCellFill}>{st('Competency')}</Text>
                            </View>
                            {both && (
                                <View style={styles.tableCol}>
                                    <Text style={styles.tableCell}>{st('Manager')}</Text>
                                </View>
                            )}
                            {both && (
                                <View style={styles.tableCol}>
                                    <Text style={styles.tableCell}>{st('Employee')}</Text>
                                </View>
                            )}
                            <View style={styles.tableCol}>
                                <Text style={styles.tableCell}>{st('Rating')}</Text>
                            </View>
                        </View>
                        {/* TableContent */}
                        {json.competencies.map(c => {
                            const managerPredictions = JSON.parse(appraisal.manager_Predictions || '[]');
                            const userPredictions = JSON.parse(appraisal.user_Predictions || '[]');

                            c.staff = c.staff || userPredictions.find(x => x.competency_Id === c.id)?.level;
                            c.manager = c.manager || managerPredictions.find(x => x.competency_Id === c.id)?.level;

                            const agreedLevel = agreedValues.some(x => x.id === c.id)
                                ? agreedValues.find(x => x.id === c.id)?.level
                                : 0;

                            const showPis = c.positiveIndicators?.length > 0 && options.includePositiveIndicatorsInReviewPDF;

                            return (
                                <>
                                    <View
                                        wrap={false}
                                        key={`table-${appraisal.id}-${c.competency_Id}-${c.id}`}
                                        style={styles.tableRow}
                                    >
                                        <View style={showPis ? styles.tableColFlagNoEnd : styles.tableColFlag}>
                                            <Image
                                                style={styles.flag}
                                                src={c.flagged ? '/dist/img/flagged.jpg' : '/dist/img/notflagged.jpg'}
                                            />
                                        </View>
                                        <View style={showPis ? styles.tableColFillNoEnd : styles.tableColFill}>
                                            <Text style={styles.tableCellFill}>{`${c.area} - ${c.name}`}</Text>
                                        </View>
                                        {both && (
                                            <View style={showPis ? styles.tableColNoEnd : styles.tableCol}>
                                                <Text style={styles.tableCell}>{c.manager} / 5</Text>
                                            </View>
                                        )}
                                        {both && (
                                            <View style={showPis ? styles.tableColNoEnd : styles.tableCol}>
                                                <Text style={styles.tableCell}>{c.staff} / 5</Text>
                                            </View>
                                        )}
                                        <View style={showPis ? styles.tableColNoEnd : styles.tableCol}>
                                            <Text style={styles.tableCell}>{agreedLevel} / 5</Text>
                                        </View>
                                    </View>
                                    {showPis && (
                                        <View
                                            wrap={false}
                                            key={`table-${appraisal.id}-${c.competency_Id}-${c.id}`}
                                            style={styles.tableRow}
                                        >
                                            <View style={styles.tableColFlag}></View>
                                            <View style={styles.tableColFill}>
                                                {c.positiveIndicators.map((pi, i) => {
                                                    const m = managerPredictions.find(x => x.competency_Id === c.id)
                                                        ?.positiveIndicators[(i + 1).toString()];
                                                    const u = userPredictions.find(x => x.competency_Id === c.id)
                                                        ?.positiveIndicators[(i + 1).toString()];
                                                    return (
                                                        <View style={styles.tableColExtra}>
                                                            <View
                                                                style={
                                                                    both
                                                                        ? styles.tableColExtraTextBoth
                                                                        : styles.tableColExtraText
                                                                }
                                                            >
                                                                <Text style={styles.textSmall}>{pi.statement}</Text>
                                                            </View>
                                                            {user && (
                                                                <>
                                                                    <View style={styles.tableColExtraIcon}>
                                                                        <Image
                                                                            style={styles.icon}
                                                                            src="/dist/icons/user.png"
                                                                        ></Image>
                                                                    </View>
                                                                    <View style={styles.tableColExtraRatings}>
                                                                        <Text style={styles.textSmall}>{u} / 5</Text>
                                                                    </View>
                                                                </>
                                                            )}
                                                            {manager && (
                                                                <>
                                                                    <View style={styles.tableColExtraIcon}>
                                                                        <Image
                                                                            style={styles.icon}
                                                                            src="/dist/icons/manager.png"
                                                                        ></Image>
                                                                    </View>
                                                                    <View style={styles.tableColExtraRatings}>
                                                                        <Text style={styles.textSmall}>{m} / 5</Text>
                                                                    </View>
                                                                </>
                                                            )}
                                                        </View>
                                                    );
                                                })}
                                            </View>
                                        </View>
                                    )}
                                </>
                            );
                        })}
                    </View>
                </View>
            </Page>
            <Page size="A4" style={styles.page}>
                {both && (
                    <View wrap={false} style={styles.infoBox}>
                        <View>
                            <Text style={styles.label}>{st('ReviewComments')}</Text>
                            <Text style={styles.labelValue}>{appraisal.comments}</Text>
                        </View>
                    </View>
                )}
                {both && (
                    <View wrap={false} style={styles.infoBox}>
                        <View>
                            <Text style={styles.label}>
                                {st(`ManagerComments${hasReviewCompetencies ? '/General' : ''}`)}
                            </Text>
                            <Text style={styles.labelValue}>{appraisal.manager_Comments}</Text>
                        </View>
                    </View>
                )}
                {both && (
                    <View wrap={false} style={styles.infoBox}>
                        <View>
                            <Text style={styles.label}>
                                {st(`EmployeeComments${hasReviewCompetencies ? '/General' : ''}`)}
                            </Text>
                            <Text style={styles.labelValue}>{appraisal.user_Comments}</Text>
                        </View>
                    </View>
                )}
                {json.competencies.map((competency, i) => {
                    if (!competency.review_Comment) return null;
                    const commentJson = appraisal.user_Competency_Comments;
                    let value = '';
                    if (commentJson) {
                        const comments = JSON.parse(commentJson);
                        const values = comments.find(c => (c.id || c.competency_Id) === competency.id);
                        value = values ? values.comments : '';
                    }

                    const commentJson2 = appraisal.manager_Competency_Comments;
                    let value2 = '';
                    if (commentJson2) {
                        const comments2 = JSON.parse(commentJson2);
                        const values2 = comments2.find(c => (c.id || c.competency_Id) === competency.id);
                        value2 = values2 ? values2.comments : '';
                    }
                    return (
                        <View key={`competency-${competency.id}-${appraisal.id}-${i}`}>
                            {manager && (
                                <View wrap={false} style={styles.infoBox}>
                                    <View>
                                        <Text style={styles.label}>{st('ManagerCommentsFor', competency.full_Name)}</Text>
                                        <Text style={styles.labelValue}>{value2 || '-'}</Text>
                                    </View>
                                </View>
                            )}
                            {user && (
                                <View wrap={false} style={styles.infoBox}>
                                    <View>
                                        <Text style={styles.label}>{st('EmployeeCommentsFor', competency.full_Name)}</Text>
                                        <Text style={styles.labelValue}>{value || '-'}</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    );
                })}
            </Page>
            {objectives.results && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.infoBox}>
                        <View>
                            <Text style={styles.label}>{st('Objectives')}</Text>
                            <Text style={styles.labelValue}>{st('ObjectivesAssigned', appraisal.full_Name, dateRange)}</Text>
                        </View>
                    </View>
                    <View style={styles.infoBox}>
                        <View style={styles.table}>
                            {/* TableHeader */}
                            <View style={styles.tableRow}>
                                <View style={styles.tableColFill}>
                                    <Text style={styles.tableCellFill}>{st('Objective')}</Text>
                                </View>
                                <View style={styles.tableColDate}>
                                    <Text style={styles.tableCell}>{st('Due')}</Text>
                                </View>
                                <View style={styles.tableColDate}>
                                    <Text style={styles.tableCell}>{st('Completed')}</Text>
                                </View>
                            </View>
                            {/* TableContent */}
                            {objectives.results.length ? (
                                objectives.results.map(o => {
                                    return (
                                        <View
                                            wrap={false}
                                            key={`objectives-${appraisal.id}-${o.id}-${o.date_Completed}`}
                                            style={styles.tableRow}
                                        >
                                            <View style={styles.tableColFill}>
                                                <Text style={styles.tableCellFill}>{o.name}</Text>
                                            </View>
                                            <View style={styles.tableColDate}>
                                                <Text style={styles.tableCell}>{e4Format(o.due)}</Text>
                                            </View>
                                            <View style={styles.tableColDate}>
                                                <Text style={styles.tableCell}>
                                                    {o.date_Completed ? e4Format(o.date_Completed) : '-'}
                                                </Text>
                                            </View>
                                        </View>
                                    );
                                })
                            ) : (
                                <View key={`${appraisal.id}-no-objs`} style={styles.tableRow}>
                                    <View style={styles.tableColFill}>
                                        <Text style={styles.tableCellFill}>{st('NoObjectives')}</Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    </View>
                </Page>
            )}
        </Document>
    );
};

export const PDF = ({ appraisal, prism }) => {
    const [loading, setLoading] = React.useState(true);
    const [objectives, setObjectives] = React.useState({});

    const user = useSelector(state => state.user.user);

    const st = (path, ...rest) => SystemText(`Reviews/Appraisals/PDF/${path}`, ...rest);

    React.useEffect(() => {
        if (user.companySettings?.development?.includeObjectivesInReviewPDF) {
            apiGet(`appraisal/${appraisal.profile_Id}/${appraisal.id}/objectives`, user.guid, response => {
                setObjectives(response);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [user]);

    if (loading) return false;

    return (
        <div>
            <PDFDownloadLink
                key={`pdf-${appraisal.id}`}
                document={
                    <ReviewPDF appraisal={appraisal} objectives={objectives} options={user.companySettings?.development} />
                }
                fileName={`Richardson-Insights-Capability-Review-${appraisal.id}-${appraisal.full_Name.replace(
                    ' ',
                    '-'
                )}-${e4Format(appraisal.submission, e4Formats.urlSafe)}.pdf`}
            >
                {({ blob, url, loading, error }) =>
                    error ? (
                        `ERROR:${error}`
                    ) : loading ? (
                        st('LoadingDoc')
                    ) : (
                        <button className={`btn btn-info ${prism ? 'btn-cornered' : ''}`} data-testid="button-pdf">
                            {!prism && <i className="fa fa-file-pdf mr5" />}
                            {SystemText('Reviews/Buttons/PDF')}
                        </button>
                    )
                }
            </PDFDownloadLink>
        </div>
    );
};

export default React.memo(PDF);
